/**
 * `/desk` (lane C5, docs/CONVERSATIONS.md "What the web renders"): the primary way a signed-in user works with their
 * agents. Left the thread list (the desk first, then one thread per agent), right the open thread. Below 768 px the
 * list and the thread are two screens. `/desk?thread=<id>&seq=<n>` is the stable deep link push notifications use.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { Dialog, Icon, Skeleton, cx } from '@algoboo/ui'
import { myAgentsQuery, useMyAgents } from '../lib/api'
import { prefetch, prefetchSignedIn } from '../lib/route-data'
import { usePageMeta } from '../lib/meta'
import { currentNavMode, useNavMode } from '../lib/nav'
import { useSession, SignInPrompt } from '../components/session'
import { useApiMutation } from '../lib/api/hooks/mutations'
import { errorCopy, isApiError } from '../lib/api/errors'
import { threadFeedQuery, threadsQuery, useSeen, useThreadDetail, useThreads } from '../lib/api/conversations'
import { ThreadList } from '../components/chat/ThreadList'
import { ThreadView } from '../components/chat/ThreadView'
import { Starters } from '../components/chat/Starters'
import { AgentsOverview } from '../components/owner/AgentsOverview'
import { ChainTease } from '../components/chain'

export const Route = createFileRoute('/desk')({
  validateSearch: z.object({
    thread: z.string().uuid().optional().catch(undefined),
    seq: z.coerce.number().int().positive().optional().catch(undefined),
    /** `agents`: the overview of the user's agents. */
    view: z.enum(['agents']).optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ thread: search.thread, seq: search.seq, view: search.view }),
  // the thread list, the user's agents and the thread that opens: the deep-linked one, or the desk beside the list
  loader: ({ context: { queryClient: qc }, deps }) =>
    prefetchSignedIn(qc, () => [
      qc.ensureInfiniteQueryData(myAgentsQuery()),
      qc.ensureQueryData(threadsQuery('all')).then((list) => {
        if (deps.view === 'agents' && !deps.thread) return
        const desk = list.items.find((t) => t.kind === 'desk' && t.state === 'open') ?? list.items.find((t) => t.kind === 'desk')
        const t = list.items.find((x) => x.id === (deps.thread ?? (currentNavMode() !== 'tabs' ? desk?.id : undefined)))
        if (t) prefetch(qc.ensureQueryData(threadFeedQuery(qc, t, deps.thread === t.id ? (deps.seq ?? null) : null)))
      }),
    ]),
  component: DeskPage,
})

function DeskPage() {
  usePageMeta('Desk', 'Talk to your agents: ask, decide on their proposals, and follow what they do.')
  const { me, loading } = useSession()
  if (loading) return <Skeleton className="m-4 h-[60vh] rounded-xl" />
  if (!me) return <div className="mx-auto max-w-[720px] px-4 pt-6"><SignInPrompt title="Your desk" body="Tell your agents what you want, approve what they propose, and follow their work in one conversation. Sign in to open your desk." /></div>
  return <Desk userId={me.user.id} />
}

function Desk({ userId }: { userId: string }) {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/desk' })
  const twoPane = useNavMode() !== 'tabs'
  const threads = useThreads('all')
  const seen = useSeen(userId)
  const items = threads.data?.items ?? []
  const desk = items.find((t) => t.kind === 'desk' && t.state === 'open') ?? items.find((t) => t.kind === 'desk') ?? null

  // the desk exists once per user: open it (idempotent) when the list has none
  const createDesk = useApiMutation('createThread', { invalidate: () => [['me', 'threads', 'list']] })
  const asked = useRef(false)
  useEffect(() => {
    if (threads.isSuccess && !desk && !asked.current) {
      asked.current = true
      createDesk.mutate({ body: { kind: 'desk' } })
    }
  }, [threads.isSuccess, desk, createDesk])
  const deskThread = desk ?? createDesk.data ?? null

  const overview = search.view === 'agents' && !search.thread
  const wanted = overview ? null : (search.thread ?? (twoPane ? (deskThread?.id ?? null) : null))
  const listed = items.find((t) => t.id === wanted) ?? (deskThread?.id === wanted ? deskThread : null)
  const detail = useThreadDetail(wanted && !listed ? wanted : null)
  const active = listed ?? detail.data ?? null

  const [newAgent, setNewAgent] = useState(false)
  const back = () => void navigate({ search: {} })

  // "My agents" live here (lane IA): agents without a conversation yet are listed under the threads; a tap opens one
  const mine = useMyAgents(undefined, true)
  const threaded = new Set(items.filter((t) => t.kind === 'agent').map((t) => t.agent?.id))
  const unthreaded = (mine.data?.pages.flatMap((p) => p.items) ?? []).filter((a) => a.status !== 'retired' && !threaded.has(a.id))
  const openAgent = useApiMutation('createThread', { invalidate: () => [['me', 'threads', 'list']] })
  const listError = threads.error ?? createDesk.error
  const list = (
    <div className="h-full overflow-y-auto p-2">
      {threads.isLoading ? (
        <div className="space-y-2 p-2">
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
        </div>
      ) : listError ? (
        <p role="alert" className="p-3 text-[13px] text-danger">
          {isApiError(listError) ? errorCopy(listError.code).body : 'Could not load your conversations.'}
        </p>
      ) : (
        <ThreadList
          threads={deskThread && !items.some((t) => t.id === deskThread.id) ? [deskThread, ...items] : items}
          activeId={active?.id ?? null}
          seen={seen}
          onNew={() => setNewAgent(true)}
          agents={unthreaded}
          overview={{ active: overview, count: mine.data?.pages[0]?.items.length ?? 0 }}
          opening={openAgent.isPending ? (openAgent.variables?.body.kind === 'agent' ? openAgent.variables.body.agentId : null) : null}
          onOpenAgent={(a) =>
            openAgent.mutate(
              { body: { kind: 'agent', agentId: a.id } },
              { onSuccess: (t) => void navigate({ search: { thread: t.id } }) },
            )
          }
        />
      )}
    </div>
  )

  const threadPane = overview ? (
    <AgentsOverview onBack={twoPane ? undefined : back} />
  ) : active ? (
    <ThreadView key={active.id} thread={active} userId={userId} focusSeq={search.thread === active.id ? (search.seq ?? null) : null} onBack={twoPane ? undefined : back} />
  ) : wanted && detail.error ? (
    <div className="p-6 text-[13.5px] text-ink-3" role="alert">
      {isApiError(detail.error) && detail.error.code === 'NOT_FOUND' ? 'This conversation does not exist or is not yours.' : 'Could not open this conversation.'}{' '}
      <button type="button" className="underline" onClick={back}>
        Back to the desk
      </button>
    </div>
  ) : (
    <div className="space-y-3 p-6">
      <Skeleton className="h-8 w-1/3 rounded-lg" />
      <Skeleton className="h-24 rounded-xl" />
    </div>
  )

  return (
    // the shell locks the page to the viewport on /desk (main[data-fill]): the desk fills it and only the panes scroll
    <div data-testid="desk" className="flex h-full min-h-0 bg-bg">
      {(twoPane || (!wanted && !overview)) && <aside className={cx('min-h-0 shrink-0 bg-surface/40', twoPane ? 'w-[272px] border-r border-line' : 'w-full')}>{list}</aside>}
      {(twoPane || wanted || overview) && <div className="min-h-0 min-w-0 flex-1">{threadPane}</div>}
      <Dialog open={newAgent} onClose={() => setNewAgent(false)} title="New agent" description="Pick a starting description. You name it, pick a ticker and a look, then launch it with its token; it trades in dry run first.">
        <Starters compact mode="create" onPick={() => setNewAgent(false)} />
        <ChainTease className="mt-4" />
        <Link to="/create" onClick={() => setNewAgent(false)} className="mt-4 inline-flex min-h-11 items-center gap-1 text-[13px] font-medium text-accent hover:underline md:min-h-0" data-testid="desk-create-link">
          Or set one up step by step <Icon name="chevron-right" size={13} />
        </Link>
      </Dialog>
    </div>
  )
}

